import { z } from "zod"

export const deliveryScopeTypeSchema = z.enum([
  "message_agent",
  "email_agent",
  "voice_agent",
])

export const merchandisePaymentPolicySchema = z.enum([
  "prepayment_only",
  "cash_on_delivery_only",
  "customer_choice",
])

export const agentMerchandisePaymentPolicySchema = z.union([
  z.literal("inherit_tenant_default"),
  merchandisePaymentPolicySchema,
])

export const selectedMerchandisePaymentMethodSchema = z.enum([
  "prepayment",
  "cash_on_delivery",
])

export const deliveryPriceResponsibilitySchema = z.enum([
  "business",
  "customer",
])

export const DELIVERY_TOOL_NAMES = [
  "resolve_product_price",
  "delivery_search_locations",
  "delivery_quote_shipment",
  "delivery_create_shipment",
  "delivery_track_shipment",
] as const

const DELIVERY_TOOL_NAME_SET = new Set<string>(DELIVERY_TOOL_NAMES)

export const deliveryToolSettingsSchema = z.object({
  resolve_product_price: z.boolean(),
  delivery_search_locations: z.boolean(),
  delivery_quote_shipment: z.boolean(),
  delivery_create_shipment: z.boolean(),
  delivery_track_shipment: z.boolean(),
})

export const DEFAULT_DELIVERY_TOOL_SETTINGS = {
  resolve_product_price: true,
  delivery_search_locations: true,
  delivery_quote_shipment: true,
  delivery_create_shipment: true,
  delivery_track_shipment: true,
} as const satisfies DeliveryToolSettings

export function resolveAgentDeliveryEnabled(
  rawConfig: unknown,
  legacyExplicitBindingState?: "enabled" | "disabled",
): boolean {
  if (rawConfig && typeof rawConfig === "object" && !Array.isArray(rawConfig)) {
    const configured = (rawConfig as Record<string, unknown>).delivery_enabled
    if (typeof configured === "boolean") return configured
  }
  return legacyExplicitBindingState === "enabled"
}

export function isDeliveryToolName(value: string): value is DeliveryToolName {
  return DELIVERY_TOOL_NAME_SET.has(value)
}

export function isDeliveryToolEnabled(
  settings: DeliveryToolSettings | undefined,
  toolName: string,
): boolean {
  if (!isDeliveryToolName(toolName)) return false
  if (!isRawDeliveryToolEnabled(settings, toolName)) return false
  switch (toolName) {
    case "delivery_create_shipment":
      return isRawDeliveryToolEnabled(settings, "delivery_quote_shipment")
    default:
      return true
  }
}

export function allowsAgentEnteredDeliveryPrices(rawConfig: unknown): boolean {
  if (!rawConfig || typeof rawConfig !== "object" || !("delivery_tools" in rawConfig)) return false
  const tools = rawConfig.delivery_tools
  return Boolean(tools && typeof tools === "object"
    && "resolve_product_price" in tools && tools.resolve_product_price === false)
}

export function hasEnabledDeliveryTool(
  settings: DeliveryToolSettings | undefined,
): boolean {
  return DELIVERY_TOOL_NAMES.some(toolName => isDeliveryToolEnabled(settings, toolName))
}

export function hasEnabledProviderDeliveryTool(
  settings: DeliveryToolSettings | undefined,
): boolean {
  return DELIVERY_TOOL_NAMES.some(toolName =>
    toolName !== "resolve_product_price"
    && isDeliveryToolEnabled(settings, toolName))
}

function isRawDeliveryToolEnabled(
  settings: DeliveryToolSettings | undefined,
  toolName: DeliveryToolName,
): boolean {
  return settings?.[toolName] ?? DEFAULT_DELIVERY_TOOL_SETTINGS[toolName]
}

const contactSchema = z.object({
  name: z.string().trim().min(1),
  phone: z.string().trim().min(1),
  email: z.string().trim().email().optional(),
  countryCode: z.string().trim().length(2).transform(value => value.toUpperCase()),
  companyName: z.string().trim().min(1).optional(),
  companyTin: z.string().trim().min(1).optional(),
})

const providerLocationSchema = z.object({
  type: z.enum(["branch", "locker", "pickup_point"]),
  countryCode: z.string().trim().length(2).transform(value => value.toUpperCase()),
  providerLocationId: z.string().trim().min(1),
  providerLocationCode: z.string().trim().min(1).optional(),
  displayName: z.string().trim().min(1).optional(),
  city: z.string().trim().min(1).max(500).optional(),
  region: z.string().trim().min(1).max(500).optional(),
})

const addressLocationSchema = z.object({
  type: z.literal("address"),
  countryCode: z.string().trim().length(2).transform(value => value.toUpperCase()),
  city: z.string().trim().min(1),
  region: z.string().trim().min(1).optional(),
  street: z.string().trim().min(1),
  building: z.string().trim().min(1),
  postalCode: z.string().trim().min(1),
  apartment: z.string().trim().min(1).optional(),
  block: z.string().trim().min(1).optional(),
  note: z.string().trim().min(1).optional(),
})

export const deliverySenderSchema = contactSchema.extend({
  providerIdentity: z.object({
    partyReference: z.string().trim().min(1),
    contactReference: z.string().trim().min(1),
  }).strict().optional(),
  location: z.discriminatedUnion("type", [
    providerLocationSchema,
    addressLocationSchema,
  ]),
})

const providerConfigSchema = z.object({
  environment: z.enum(["production", "sandbox"]).default("production"),
  baseUrl: z.string().url().optional(),
  directoryBaseUrl: z.string().url().optional(),
}).default({ environment: "production" })

export const deliveryIntegrationConfigSchema = z.object({
  version: z.literal(1),
  providerConfig: providerConfigSchema,
  sender: deliverySenderSchema,
  defaults: z.object({
    deliveryPaymentPayer: z.enum(["sender", "recipient", "third_party"])
      .default("sender"),
    payerContractReference: z.string().trim().min(1).optional(),
    cashOnDeliverySettlementProfileReference: z.string().trim().min(1).optional(),
  }).default({ deliveryPaymentPayer: "sender" }),
})

export const deliveryCrmSignalSchema = z.object({
  stageId: z.string().trim().min(1).optional(),
  tags: z.array(z.string().trim().min(1)).max(50).optional(),
}).refine(value => value.stageId || (value.tags?.length ?? 0) > 0, {
  message: "CRM signal must contain a stage or at least one tag",
})

const crmFieldCodeSchema = z.string().trim().min(1).max(200)

export const deliveryCrmFieldMappingSchema = z.object({
  shippingAddressFieldCode: crmFieldCodeSchema.optional(),
  writeMerchandiseTotalToOpportunityPrice: z.boolean().optional(),
  paymentMethod: z.object({
    fieldCode: crmFieldCodeSchema,
    prepaymentValue: z.string().trim().min(1).max(200),
    cashOnDeliveryValue: z.string().trim().min(1).max(200),
  }).strict().optional(),
  deliveryMethod: z.object({
    fieldCode: crmFieldCodeSchema,
    value: z.string().trim().min(1).max(200),
  }).strict().optional(),
  customFields: z.object({
    trackingNumber: crmFieldCodeSchema.optional(),
    carrier: crmFieldCodeSchema.optional(),
    orderReference: crmFieldCodeSchema.optional(),
    paymentState: crmFieldCodeSchema.optional(),
    merchandiseTotal: crmFieldCodeSchema.optional(),
    deliveryFee: crmFieldCodeSchema.optional(),
    orderItems: crmFieldCodeSchema.optional(),
  }).strict().optional(),
}).strict()

const bindingConfigBase = z.object({
  version: z.literal(1),
  state: z.enum(["enabled", "disabled"]),
  crmSignal: deliveryCrmSignalSchema.optional(),
  crmFieldMapping: deliveryCrmFieldMappingSchema.optional(),
})

export const tenantDefaultDeliveryBindingConfigSchema = bindingConfigBase.extend({
  merchandisePaymentPolicy: merchandisePaymentPolicySchema,
})

export const agentDeliveryBindingConfigSchema = bindingConfigBase.extend({
  merchandisePaymentPolicy: agentMerchandisePaymentPolicySchema,
})

export type DeliveryScopeType = z.infer<typeof deliveryScopeTypeSchema>
export type MerchandisePaymentPolicy = z.infer<typeof merchandisePaymentPolicySchema>
export type SelectedMerchandisePaymentMethod = z.infer<
  typeof selectedMerchandisePaymentMethodSchema
>
export type DeliveryPriceResponsibility = z.infer<typeof deliveryPriceResponsibilitySchema>
export type DeliveryToolName = typeof DELIVERY_TOOL_NAMES[number]
export type DeliveryToolSettings = z.infer<typeof deliveryToolSettingsSchema>
export type DeliveryIntegrationConfig = z.infer<typeof deliveryIntegrationConfigSchema>
export type DeliveryCrmSignal = z.infer<typeof deliveryCrmSignalSchema>
export type DeliveryCrmFieldMapping = z.infer<
  typeof deliveryCrmFieldMappingSchema
>
export type TenantDefaultDeliveryBindingConfig = z.infer<
  typeof tenantDefaultDeliveryBindingConfigSchema
>
export type AgentDeliveryBindingConfig = z.infer<
  typeof agentDeliveryBindingConfigSchema
>
